// =============================================================================
// TrackerPage.jsx
//
// Alterações relativamente à v1:
//   1. Estado de filtros (filters) — filtragem puramente local, sem chamadas API
//   2. useMemo → filteredEntries — entries após aplicar todos os filtros activos
//   3. Os filtros vivem nos cabeçalhos da tabela (TrackerTable); aqui fica só
//      um resumo (chips) dos filtros ativos + "Clear all". O estado é partilhado
//      e alimenta também o TrackerGantt/TrackerCalendar.
//   4. Renderiza TrackerGantt/Calendar abaixo da tabela
//   5. As ações (New Activity, etc.) estão num botão flutuante (TrackerFab)
//
// Lógica de filtragem temporal (sobreposição de períodos):
//   uma atividade entra se [StartDate, EndDate] se sobrepõe a [From, To]
//   (sem EndDate = em aberto). Ver applyFilters.
// =============================================================================

import React, { useState, useEffect, useReducer, useCallback, useRef, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  Box, Typography, Alert, Snackbar,
  Stack, CircularProgress, Chip, Button, Switch, FormControlLabel,
  IconButton, Tooltip,
} from '@mui/material';
import AddIcon            from '@mui/icons-material/Add';
import FilterListIcon     from '@mui/icons-material/FilterList';
import FullscreenIcon     from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import TuneIcon           from '@mui/icons-material/Tune';
import DownloadIcon       from '@mui/icons-material/Download';
import TimelineIcon       from '@mui/icons-material/Timeline';
import CalendarMonthIcon  from '@mui/icons-material/CalendarMonth';

import TrackerTable    from './TrackerTable';
import TrackerModal    from './TrackerModal';
import SiteListDialog  from './SiteListDialog';
import TrackerGantt    from './TrackerGantt';
import TrackerCalendar  from './TrackerCalendar';
import TrackerSettings  from './TrackerSettings';
import BulkImportDialog from './BulkImportDialog';
import TrackerFab       from './TrackerFab';

import trackerReducer                                  from '../reducer/trackerReducer';
import { loadEntries, loadEntry, saveEntry, removeEntry } from '../reducer/trackerThunks';
import { openModal, closeModal, clearError }           from '../actions/trackerActions';
import { apiExportAll, apiExportFiltered, apiGetFeatures, apiGetPrivilege } from '../requests/trackerRequests';
import { apiCanImport } from '../requests/importRequests';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { getDisplayStatus, STATUS_VALUES } from '../utils/status';


// =============================================================================
// useThunkReducer — suporta thunks (funções) além de actions (objectos)
// =============================================================================
const useThunkReducer = (reducer, initialState) => {
  const [state, rawDispatch] = useReducer(reducer, initialState);

  const dispatch = useCallback(
    (action) => {
      if (typeof action === 'function') {
        action(dispatch);
      } else {
        rawDispatch(action);
      }
    },
    [rawDispatch]
  );

  return [state, dispatch];
};


// ── State inicial do reducer ──────────────────────────────────────────────────
const initialState = {
  entries:     [],
  activeEntry: null,
  loading:     false,
  saving:      false,
  loadingOne:  false,
  error:       null,
  modalOpen:   false,
};

// ── State inicial dos filtros ─────────────────────────────────────────────────
// A visibilidade das Closed é controlada pelo toggle "Show closed" (showClosed,
// por defeito false → Closed escondidas). O filtro Status já não pré-selecciona
// nada. Como o showClosed vive no próprio estado dos filtros, o "Clear all"
// (que repõe defaultFilters) também repõe o toggle a off — limpar filtros nunca
// faz reaparecer as Closed. Só aparecem quando o utilizador liga o toggle; e o
// "Closed" só surge como opção no filtro Status quando o toggle está ligado.
const emptyFilters = {
  title:        '',     // pesquisa por título (coluna Activity)
  showClosed:   false,  // toggle "Show closed" (off = esconde Closed)
  // Filtro de data por coluna, estilo Excel: { type, dates[], from, to }.
  // type: 'all' | 'today' | 'thisWeek' | 'thisMonth' | 'thisYear'
  //       | 'before' | 'after' | 'between' | 'list'
  startFilter:  { type: 'all', dates: [], from: '', to: '' },   // coluna Start
  endFilter:    { type: 'all', dates: [], from: '', to: '' },   // coluna End
  technologies: [],     // string[]
  teams:        [],     // string[]
  owners:       [],     // string[]
  features:     [],     // string[]
  vendors:      [],     // string[]
  sides:        [],     // string[] — 'East' | 'West' | 'None' (ver SIDE_BUCKET)
  statuses:     [],     // string[] — 'In Progress' | 'Planned' | 'Overdue' | 'Closed'
};

// defaultFilters = estado inicial e alvo do "Clear all" (tudo vazio, toggle off).
const defaultFilters = { ...emptyFilters };

// Side (TFinTF/TFinVF/VFinTF, guardado por site em server.tacs[].siteSides)
// agrupado em dois "buckets" de negócio, por especificação:
//   West → TFinVF
//   East → TFinTF e VFinTF
// Uma atividade pode ter sites de vários buckets ao mesmo tempo (um único
// OMC_ID pode ter sites com Side diferente — já confirmado por SQL), por
// isso o filtro é "a atividade tem pelo menos um site neste bucket", não
// "todos os sites são deste bucket".
const SIDE_BUCKET = { TFinTF: 'East', VFinTF: 'East', TFinVF: 'West' };

const getEntrySideBuckets = (entry) => {
  const buckets = new Set();
  (entry.servers ?? []).forEach((s) =>
    (s.tacs ?? []).forEach((t) =>
      Object.values(t.siteSides ?? {}).forEach((side) => {
        const bucket = SIDE_BUCKET[side];
        if (bucket) buckets.add(bucket);
      })
    )
  );
  return buckets;
};

// 'None' e um valor sentinela (nao vem de SIDE_BUCKET): representa
// atividades sem NENHUM site com Side resolvido (sem servers, ou servers/
// sites sem Side gravado). Pedido do utilizador para poder isolar estes
// casos no filtro, tal como ja aparecem destacados ("—") na tabela.
const entryHasSideValue = (entry, value) => {
  const buckets = getEntrySideBuckets(entry);
  return value === 'None' ? buckets.size === 0 : buckets.has(value);
};

const initialSnackbar = { open: false, message: '', severity: 'success' };


// =============================================================================
// Helpers de filtragem
// =============================================================================

// Converte string de data da API (vários formatos) → timestamp UTC
const parseDateMs = (raw) => {
  if (!raw) return null;
  const s = String(raw).trim();

  const slash = s.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
  if (slash) return new Date(`${slash[3]}-${slash[1]}-${slash[2]}T00:00:00Z`).getTime();

  const dash = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (dash) {
    const [, y, a, b] = dash;
    const iso = Number(a) > 12 ? `${y}-${b}-${a}` : `${y}-${a}-${b}`;
    return new Date(`${iso}T00:00:00Z`).getTime();
  }

  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d.getTime();
};

// Normaliza a data de uma entry para a chave 'yyyy-MM-dd' usada nas listas de
// datas (filtros das colunas Start/End). Usa o mesmo parse dos restantes
// filtros para garantir que a opção mostrada bate certo com a filtragem.
const toDateKey = (raw) => {
  const ms = parseDateMs(raw);
  return ms == null ? null : new Date(ms).toISOString().slice(0, 10);
};

// Data local (hoje/semana/mês/ano do utilizador) em 'yyyy-MM-dd'. Comparações
// feitas como strings ISO — ordenam cronologicamente e não têm problemas de
// fuso, já que as chaves das atividades (toDateKey) são a data-calendário.
const localYMD = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const mondayIndex = (d) => (d.getDay() + 6) % 7;   // 0 = segunda

// Avalia o filtro de data (estilo Excel) de uma coluna contra a data da entry.
const matchDateFilter = (f, raw) => {
  const type = f?.type ?? 'all';
  if (type === 'all') return true;

  const key = toDateKey(raw);
  if (!key) return false;                 // sem data não passa nenhum filtro específico

  const now = new Date();
  switch (type) {
    case 'list':     return (f.dates?.length ? f.dates.includes(key) : true);
    case 'before':   return f.from ? key < f.from : true;
    case 'after':    return f.from ? key > f.from : true;
    case 'between':  return (!f.from || key >= f.from) && (!f.to || key <= f.to);
    case 'today':    return key === localYMD(now);
    case 'thisMonth':return key.slice(0, 7) === localYMD(now).slice(0, 7);
    case 'thisYear': return key.slice(0, 4) === localYMD(now).slice(0, 4);
    case 'thisWeek': {
      const mon = new Date(now); mon.setDate(now.getDate() - mondayIndex(now));
      const sun = new Date(mon); sun.setDate(mon.getDate() + 6);
      return key >= localYMD(mon) && key <= localYMD(sun);
    }
    default: return true;
  }
};

// Aplica todos os filtros activos a um array de entries
// excludeKey (opcional) — ignora o próprio filtro desse campo, mantendo os
// restantes activos. Usado por deriveFilterOptions para calcular, para cada
// dropdown, só as opções que continuam possíveis dado o que já está
// seleccionado nos OUTROS filtros (faceted search / cascata entre todos os
// filtros, incluindo o Side).
const applyFilters = (entries, filters, excludeKey = null) => {
  // Dois intervalos de data INDEPENDENTES, um por coluna (pedido do
  // utilizador): a coluna Start filtra a Start Date, a coluna End filtra a
  // End Date. Não há lógica de sobreposição — cada um funciona por si.
  const titleQ = (filters.title ?? '').trim().toLowerCase();

  return entries.filter((e) => {
    // ── Gate das Closed (toggle "Show closed") ──────────────────────────────
    // Independente dos restantes filtros: com o toggle off, as atividades
    // Closed nunca aparecem (nem sequer alimentam as opções dos dropdowns).
    if (!filters.showClosed && getDisplayStatus(e) === 'Closed') return false;

    // ── Pesquisa por título (coluna Activity) ───────────────────────────────
    if (titleQ && !(e.title ?? '').toLowerCase().includes(titleQ)) return false;

    // ── Filtro da coluna Start (estilo Excel) ───────────────────────────────
    if (excludeKey !== 'startFilter' && !matchDateFilter(filters.startFilter, e.startDate)) return false;

    // ── Filtro da coluna End (estilo Excel) ─────────────────────────────────
    if (excludeKey !== 'endFilter' && !matchDateFilter(filters.endFilter, e.endDate)) return false;

    // ── Filtro de tecnologia ──────────────────────────────────────────────────
    if (excludeKey !== 'technologies' && filters.technologies.length > 0) {
      if (!filters.technologies.includes(e.technology)) return false;
    }

    // ── Filtro de equipa ──────────────────────────────────────────────────────
    if (excludeKey !== 'teams' && filters.teams.length > 0) {
      if (!filters.teams.includes(e.teamName)) return false;
    }

    // ── Filtro de owner ───────────────────────────────────────────────────────
    if (excludeKey !== 'owners' && filters.owners.length > 0) {
      const entryOwners = e.ownerNames ?? [];
      const hasMatch = entryOwners.some((o) => filters.owners.includes(o));
      if (!hasMatch) return false;
    }

    // ── Filtro de feature ─────────────────────────────────────────────────────
    if (excludeKey !== 'features' && filters.features.length > 0) {
      if (!filters.features.includes(e.feature)) return false;
    }

    if (excludeKey !== 'vendors' && filters.vendors.length > 0) {
      if (!filters.vendors.includes(e.vendor)) return false;
    }

    // ── Filtro de Side (East/West/None) ─────────────────────────────────────
    if (excludeKey !== 'sides' && filters.sides.length > 0) {
      const hasMatch = filters.sides.some((b) => entryHasSideValue(e, b));
      if (!hasMatch) return false;
    }

    // ── Filtro de Status (In Progress/Planned/Overdue/Closed) ───────────────
    // Substitui o antigo toggle "Show Closed": vazio = sem filtragem (mostra
    // tudo, incluindo Closed); com selecção = só os estados escolhidos.
    if (excludeKey !== 'statuses' && filters.statuses.length > 0) {
      if (!filters.statuses.includes(getDisplayStatus(e))) return false;
    }

    return true;
  });
};

// Valores fixos do dropdown Side (classificação de negócio, não derivada
// dos dados — ver SIDE_BUCKET acima).
const SIDE_VALUES = ['East', 'West', 'None'];

// Deriva, para cada dropdown, só as opções que ainda fazem sentido dado o
// que já está seleccionado nos RESTANTES filtros (cada campo é calculado
// ignorando o seu próprio filtro, para nunca ficar preso incapaz de alargar
// de novo). Ex: se Side=West já estiver escolhido e não houver nenhuma
// atividade 2G do lado West, "2G" deixa de aparecer no Technology.
const deriveFilterOptions = (entries, filters) => {
  const techs    = [...new Set(applyFilters(entries, filters, 'technologies').map((e) => e.technology).filter(Boolean))].sort();
  const teams    = [...new Set(applyFilters(entries, filters, 'teams').map((e) => e.teamName).filter(Boolean))].sort();
  const owners   = [...new Set(applyFilters(entries, filters, 'owners').flatMap((e) => e.ownerNames ?? []).filter(Boolean))].sort();
  const features = [...new Set(applyFilters(entries, filters, 'features').map((e) => e.feature).filter(Boolean))].sort();
  const vendors  = [...new Set(applyFilters(entries, filters, 'vendors').map((e) => e.vendor).filter(Boolean))].sort();

  const sidesPool = applyFilters(entries, filters, 'sides');
  const sides = SIDE_VALUES.filter((s) => sidesPool.some((e) => entryHasSideValue(e, s)));

  // Status — mesma cascata: só os estados presentes no pool que passa nos
  // restantes filtros, pela ordem canónica (STATUS_VALUES).
  const statusPool = applyFilters(entries, filters, 'statuses');
  const statuses = STATUS_VALUES.filter((s) => statusPool.some((e) => getDisplayStatus(e) === s));

  // Datas distintas (yyyy-MM-dd) presentes, em cascata como os restantes.
  // Ordenam cronologicamente porque o formato ISO ordena lexicalmente.
  const startPool = applyFilters(entries, filters, 'startFilter');
  const startDates = [...new Set(startPool.map((e) => toDateKey(e.startDate)).filter(Boolean))].sort();
  const endPool = applyFilters(entries, filters, 'endFilter');
  const endDates = [...new Set(endPool.map((e) => toDateKey(e.endDate)).filter(Boolean))].sort();

  return { techs, teams, owners, features, vendors, sides, statuses, startDates, endDates };
};


// =============================================================================
// COMPONENTE
// =============================================================================
const TrackerPage = () => {
  const [state, dispatch] = useThunkReducer(trackerReducer, initialState);
  const { entries, activeEntry, loading, saving, loadingOne, error, modalOpen } = state;

  // userId do utilizador autenticado — enviado para a API para o audit log
  const userId = useSelector((s) => s.session.user.id);

  // ── Privilege (role + vendor + module access) ─────────────────────────────
  const [privilege, setPrivilege] = useState(null);   // null = still loading
  const role      = privilege?.role ?? null;
  const canCreate = role === null || role === 'Admin' || role === 'Regular';

  // Admin pode sempre editar/apagar. Todos os outros (exceto Tourist, que é
  // sempre read-only) só podem editar/apagar se forem o Criador OU um dos
  // Owners da atividade — tem de espelhar exatamente a regra do backend
  // (EnforceWriteAccess em ActivityTrackerController.cs).
  const isCreatorOfEntry = (entry) => String(entry?.createdBy) === String(userId);
  const isOwnerOfEntry   = (entry) => (entry?.ownerUserIds ?? []).map(String).includes(String(userId));
  const canModifyEntry   = (entry) =>
    role === null ||
    role === 'Admin' ||
    (role !== 'Tourist' && (isCreatorOfEntry(entry) || isOwnerOfEntry(entry)));

  const canEditEntry   = canModifyEntry;
  const canDeleteEntry = canModifyEntry;
  const vendor         = privilege?.vendor ?? 'Both';

  const [snackbar, setSnackbar] = useState(initialSnackbar);
  const [filters,  setFilters]  = useState(defaultFilters);
  const [tableMaximized, setTableMaximized] = useState(false);
  const [featureTypes, setFeatureTypes] = useState([]);  // [{ id, name, color }]
  const [chartView, setChartView] = useState('gantt');   // 'gantt' | 'calendar'
  const [settingsOpen,   setSettingsOpen]   = useState(false);
  const [lookupsVersion, setLookupsVersion] = useState(0);  // incremented when Settings closes

  // Bulk Import — button only shows for Admins or whoever is listed in
  // TrackerImportAuthorizedUser (see ActivityImportController.CanImport).
  const [canImport, setCanImport]           = useState(false);
  const [bulkImportOpen, setBulkImportOpen] = useState(false);

  useEffect(() => {
    apiGetFeatures()
      .then((res) => setFeatureTypes(res.data ?? []))
      .catch(() => {});
    apiCanImport()
      .then((res) => setCanImport(!!res.data?.canImport))
      .catch(() => setCanImport(false));
  }, []);

  // ── SiteListDialog — fora do TrackerModal para evitar bug de backdrops ────
  const [siteListOpen,     setSiteListOpen]     = useState(false);
  const [siteListServer,   setSiteListServer]   = useState(null);
  const [siteListCallback, setSiteListCallback] = useState(null);


  // ── Entries filtradas (memo — só recalcula quando entries ou filters mudam) ─
  const filteredEntries = useMemo(
    () => applyFilters(entries, filters),
    [entries, filters]
  );

  // ── Opções de cada dropdown, em cascata com os restantes filtros activos ──
  const filterOptions = useMemo(
    () => deriveFilterOptions(entries, filters),
    [entries, filters]
  );


  // ── Snackbar helpers ──────────────────────────────────────────────────────
  const showSnack  = (message, severity = 'success') =>
    setSnackbar({ open: true, message, severity });
  const closeSnack = () =>
    setSnackbar((prev) => ({ ...prev, open: false }));

  // ── Export ────────────────────────────────────────────────────────────────
  // Dois exports: "All" (tudo o que o vendor scope permite) e "Visible" (só as
  // entradas actualmente à vista, respeitando todos os filtros da tabela). Ambos
  // dão feedback — antes um erro do servidor passava despercebido e o botão
  // parecia não fazer nada.
  const [exporting, setExporting] = useState(false);

  const handleExportAll = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      await apiExportAll();
    } catch (err) {
      showSnack(err?.message || 'Export failed.', 'error');
    } finally {
      setExporting(false);
    }
  };

  const handleExportVisible = async () => {
    if (exporting) return;
    const ids = displayedEntries.map((e) => e.id);
    if (ids.length === 0) { showSnack('No visible activities to export.', 'error'); return; }
    setExporting(true);
    try {
      await apiExportFiltered(ids);
    } catch (err) {
      showSnack(err?.message || 'Export failed.', 'error');
    } finally {
      setExporting(false);
    }
  };


  // ── Carregar tabela ao montar ─────────────────────────────────────────────
  useEffect(() => {
    dispatch(loadEntries(vendor));

    // Fetch privilege for this user
    if (userId) {
      apiGetPrivilege(userId)
        .then((res) => {
          const data = res.data;
          if (!data) { setPrivilege({ role: 'Tourist', vendor: 'Both' }); return; }
          const hasAccess = data.moduleAccess?.find((m) => m.moduleKey === 'activitiesTracker')?.hasAccess;
          setPrivilege({
            role:   hasAccess ? (data.role ?? 'Tourist') : 'Tourist',
            vendor: data.vendor ?? 'Both',
          });
        })
        .catch(() => setPrivilege({ role: 'Tourist', vendor: 'Both' }));
    }
  }, []);


  // ── Snackbar após operações de escrita ────────────────────────────────────
  const prevSaving     = useRef(false);
  const lastOpIsDelete = useRef(false);
  useEffect(() => {
    if (prevSaving.current && !saving) {
      if (error) {
        // Popup de erro (ex: "You don't have permission to edit or delete
        // this activity...") em vez de deixar o utilizador só com o banner
        // no topo da página — mais visível para ações pontuais como Delete.
        showSnack(error, 'error');
      } else {
        showSnack(
          lastOpIsDelete.current ? 'Activity deleted.' : 'Activity saved successfully.',
          lastOpIsDelete.current ? 'info' : 'success'
        );
      }
      lastOpIsDelete.current = false;
    }
    prevSaving.current = saving;
  }, [saving, error]);  // eslint-disable-line react-hooks/exhaustive-deps


  // ── Handlers da tabela e modal ────────────────────────────────────────────

  const handleRowClick = (row) => {
    const normalizedRow = {
      ...row,
      teamId:  row.teamId  != null ? String(row.teamId)  : '',
      ownerId: row.ownerId != null ? String(row.ownerId) : '',
    };
    dispatch(openModal(normalizedRow));
    dispatch(loadEntry(row.id));
  };

  const handleAddNew     = ()         => dispatch(openModal(null));

  const handleCopy = (entry) => {
    dispatch(openModal({
      ...entry,
      id:        undefined,
      title:     `${entry.title} - Copy`,
      startDate: '',
      endDate:   '',
      phases:    [],
      comments:  '',
      status:    'Open',
    }));
  };
  const handleCloseModal = ()         => dispatch(closeModal());
  const handleSave = (formData) => {
    lastOpIsDelete.current = false;
    dispatch(saveEntry(formData, userId));
  };

  const handleDeleteFromTable = (id, entryTitle) => {
    if (!window.confirm('Remove this activity? This cannot be undone.')) return;
    lastOpIsDelete.current = true;
    dispatch(removeEntry(id, userId, entryTitle));
  };

  const handleDeleteFromModal = (id, entryTitle) => {
    lastOpIsDelete.current = true;
    dispatch(removeEntry(id, userId, entryTitle));
  };


  // ── Handlers do SiteListDialog ────────────────────────────────────────────

  const handleOpenSiteList = (server, updateServerFn) => {
    setSiteListServer(server);
    setSiteListCallback(() => updateServerFn);
    setSiteListOpen(true);
  };

  const handleCloseSiteList = () => {
    setSiteListOpen(false);
    setTimeout(() => {
      setSiteListServer(null);
      setSiteListCallback(null);
    }, 300);
  };

  const handleConfirmSiteList = (selectedSites) => {
    if (siteListCallback) siteListCallback(selectedSites);
    handleCloseSiteList();
  };


  // ── Chips de contagem por tecnologia (sobre as entries já filtradas) ──────
  // filteredEntries já reflecte todos os filtros, incluindo o gate das Closed
  // (toggle "Show closed").
  const techCounts = ['2G', '3G', '4G', '5G']
    .map((t) => ({ t, n: filteredEntries.filter((e) => e.technology === t).length }))
    .filter((x) => x.n > 0);

  const displayedEntries = filteredEntries;
  const isFiltered = displayedEntries.length < entries.length;


  // ── Chips de resumo dos filtros ativos (cada um limpa o seu grupo) ────────
  const filterChips = [];
  if (filters.title?.trim()) {
    filterChips.push({
      key: 'title',
      label: `Search: "${filters.title.trim()}"`,
      clear: () => setFilters((f) => ({ ...f, title: '' })),
    });
  }
  const dateFilterLabel = (f) => {
    switch (f?.type) {
      case 'list':      return f.dates?.length ? f.dates.join(', ') : null;
      case 'before':    return f.from ? `before ${f.from}` : null;
      case 'after':     return f.from ? `after ${f.from}` : null;
      case 'between':   return (f.from || f.to) ? `${f.from || '…'} → ${f.to || '…'}` : null;
      case 'today':     return 'Today';
      case 'thisWeek':  return 'This week';
      case 'thisMonth': return 'This month';
      case 'thisYear':  return 'This year';
      default:          return null;
    }
  };
  const emptyDateFilter = { type: 'all', dates: [], from: '', to: '' };
  const startLbl = dateFilterLabel(filters.startFilter);
  if (startLbl) {
    filterChips.push({
      key: 'startFilter',
      label: `Start: ${startLbl}`,
      clear: () => setFilters((f) => ({ ...f, startFilter: emptyDateFilter })),
    });
  }
  const endLbl = dateFilterLabel(filters.endFilter);
  if (endLbl) {
    filterChips.push({
      key: 'endFilter',
      label: `End: ${endLbl}`,
      clear: () => setFilters((f) => ({ ...f, endFilter: emptyDateFilter })),
    });
  }
  [['sides', 'Area'], ['vendors', 'Vendor'], ['technologies', 'Tech'],
   ['teams', 'Team'], ['features', 'Type'], ['statuses', 'Status'], ['owners', 'Owner']]
    .forEach(([k, lbl]) => {
      if (filters[k]?.length) {
        filterChips.push({
          key: k,
          label: `${lbl}: ${filters[k].join(', ')}`,
          clear: () => setFilters((f) => ({ ...f, [k]: [] })),
        });
      }
    });

  // ── Cabeçalho + barra de filtros ── extraído para poder ser reenviado
  // também para dentro do diálogo fullscreen da tabela (botão "Maximize"),
  // exactamente como aparece na página normal (mesmas permissões, mesmos
  // handlers, sem duplicar lógica).
  const pageToolbar = (
    <>
      {/* ── Cabeçalho ──────────────────────────────────────────────────────── */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={700} letterSpacing={-0.5} color="text.primary">
            Network Activity Tracker
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.25, display: 'block' }}>
            {isFiltered
              ? <><strong>{displayedEntries.length}</strong> of {entries.length} activities · {techCounts.map(({t,n}) => `${t}: ${n}`).join(' · ')}</>
              : <>{displayedEntries.length} {displayedEntries.length === 1 ? 'activity' : 'activities'} · {techCounts.map(({t,n}) => `${t}: ${n}`).join(' · ')}</>
            }
          </Typography>
        </Box>

        {/* Os antigos 4 botões (Export All / Settings / Bulk Import / New
            Activity) passaram para o botão flutuante (TrackerFab). Aqui fica o
            indicador de carregamento e o toggle "Show closed". */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {loadingOne && <CircularProgress size={20} />}
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={filters.showClosed}
                onChange={(e) => setFilters((f) => ({ ...f, showClosed: e.target.checked }))}
              />
            }
            label={<Typography variant="caption" color="text.secondary">Show closed</Typography>}
            labelPlacement="start"
            sx={{ mr: 0 }}
          />
          <Tooltip title={tableMaximized ? 'Restore table' : 'Maximize table'}>
            <IconButton size="small" onClick={() => setTableMaximized((v) => !v)}>
              {tableMaximized ? <FullscreenExitIcon fontSize="small" /> : <FullscreenIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        </Box>
      </Stack>

      {/* ── Banner de erro global ───────────────────────────────────────────── */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => dispatch(clearError())}>
          {error}
        </Alert>
      )}

      {/* ── Resumo dos filtros ativos ──────────────────────────────────────────
          Os filtros vivem agora nos cabeçalhos da tabela (dropdowns por coluna).
          Aqui fica só um resumo compacto do que está ativo + limpar tudo, útil
          quando se está a ver os gráficos por baixo (mesmo estado partilhado). */}
      {filterChips.length > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
          <FilterListIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
          {filterChips.map((c) => (
            <Chip key={c.key} label={c.label} size="small" onDelete={c.clear}
              sx={{ maxWidth: 360 }} />
          ))}
          {/* "Clear all" repõe a vista por defeito (Status = In Progress/
              Planned/Overdue) em vez de esvaziar tudo — assim limpar os
              filtros não faz reaparecer as Closed. Para ver Closed, seleciona
              Closed no filtro Status. */}
          <Button size="small" color="error" onClick={() => setFilters(defaultFilters)}
            sx={{ ml: 0.5, fontSize: '0.72rem' }}>
            Clear all
          </Button>
        </Box>
      )}
    </>
  );

  // Legenda dos Types — extraída para poder ser reenviada também para
  // dentro do fullscreen do Gantt (mesma lógica do maximizedHeader da
  // tabela: um único bloco reaproveitado, sem duplicar a lista).
  const legendContent = (
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap',
      flex: '1 1 320px', px: 0.5, py: 1,
    }}>
      {featureTypes.map((f) => (
        <Box key={f.id ?? f.name} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: f.color || '#9BA3AF', flexShrink: 0 }} />
          <Typography variant="caption" sx={{ fontSize: '0.68rem', color: 'text.secondary', whiteSpace: 'nowrap' }}>
            {f.name}
          </Typography>
        </Box>
      ))}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#9BA3AF', flexShrink: 0 }} />
        <Typography variant="caption" sx={{ fontSize: '0.68rem', color: 'text.secondary', whiteSpace: 'nowrap' }}>Other</Typography>
      </Box>
    </Box>
  );

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', p: { xs: 2, md: 4 } }}>

      {pageToolbar}

      {/* ── Tabela (recebe as entries JÁ filtradas) ─────────────────────────── */}
      <TrackerTable
        entries={filteredEntries}
        loading={loading || loadingOne}
        onRowClick={handleRowClick}
        onDelete={handleDeleteFromTable}
        onCopy={handleCopy}
        showClosed  /* filtro Status já decide a visibilidade das Closed */
        filters={filters}
        onFiltersChange={setFilters}
        filterOptions={filterOptions}
        isMaximized={tableMaximized}
        onMaximizeChange={setTableMaximized}
        canEditEntry={canEditEntry}
        canDeleteEntry={canDeleteEntry}
        userRole={role}
        userId={userId}
        maximizedHeader={pageToolbar}
      />

      {/* ── Divisor entre tabela e Gantt/Calendar ───────────────────────────── */}
      <Box sx={{ display: 'flex', alignItems: 'center', mt: 4, mb: 0, gap: 2 }}>
        <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
        <Typography variant="caption" color="text.disabled" sx={{ textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.62rem', fontWeight: 700, whiteSpace: 'nowrap' }}>
          {chartView === 'gantt' ? 'Gantt Chart' : 'Calendar View'}
        </Typography>
        <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
      </Box>

      {/* ── Legenda (dinâmica, a partir de featureTypes — mesma fonte de
          dados/cores do Gantt e do Calendar, editável em Tracker Settings >
          Type) + Toggle Gantt/Calendar, na mesma linha. Antes o toggle
          ficava sobreposto à legenda com margem negativa, partindo do
          princípio de que a legenda cabia sempre numa única linha — deixou
          de ser verdade assim que há mais do que ~7 Types e a legenda
          quebra para 2 linhas. Agora são dois irmãos no mesmo flex row,
          sem sobreposição, e a legenda quebra por baixo de si própria sem
          nunca invadir o toggle. ──────────────────────────────────────── */}
      <Box sx={{
        display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
        flexWrap: 'wrap', gap: 1.5, mt: 1.5,
      }}>
        {legendContent}

        <Box sx={{ display: 'flex', bgcolor: '#f0f0f0', borderRadius: '8px', p: '3px', gap: '2px', flexShrink: 0 }}>
          {[
            { value: 'gantt',    icon: <TimelineIcon sx={{ fontSize: 13 }} />,      label: 'Gantt' },
            { value: 'calendar', icon: <CalendarMonthIcon sx={{ fontSize: 13 }} />, label: 'Calendar' },
          ].map(({ value, icon, label }) => (
            <Box
              key={value}
              onClick={() => setChartView(value)}
              sx={{
                display: 'flex', alignItems: 'center', gap: 0.5,
                px: 1.4, py: 0.45, borderRadius: '6px',
                cursor: 'pointer', userSelect: 'none',
                transition: 'all 0.15s ease',
                color: chartView === value ? 'primary.main' : 'text.secondary',
                bgcolor: chartView === value ? '#ffffff' : 'transparent',
                boxShadow: chartView === value ? '0 1px 3px rgba(0,0,0,0.12)' : 'none',
              }}
            >
              {icon}
              <Typography variant="button" sx={{ lineHeight: 1, fontSize: '0.75rem' }}>{label}</Typography>
            </Box>
          ))}
        </Box>
      </Box>
      {chartView === 'gantt' ? (
        <TrackerGantt
          entries={filteredEntries}
          onRowClick={handleRowClick}
          showClosed  /* filtro Status já decide a visibilidade das Closed */
          featureTypes={featureTypes}
          legend={legendContent}
          filters={filters}
          onFiltersChange={setFilters}
          filterOptions={filterOptions}
        />
      ) : (
        <TrackerCalendar
          entries={filteredEntries}
          featureTypes={featureTypes}
          onRowClick={handleRowClick}
          showClosed  /* filtro Status já decide a visibilidade das Closed */
        />
      )}

      {/* ── Modal de criar/editar ───────────────────────────────────────────── */}
      <TrackerModal
        key={lookupsVersion}
        open={modalOpen}
        entry={activeEntry}
        saving={saving}
        loadingOne={loadingOne}
        onSave={handleSave}
        onClose={handleCloseModal}
        onDelete={handleDeleteFromModal}
        onOpenSiteList={handleOpenSiteList}
        userRole={role}
        userId={userId}
        canDeleteEntry={canDeleteEntry}
        privilegeVendor={vendor}
      />

      <TrackerSettings
        open={settingsOpen}
        onClose={() => { setSettingsOpen(false); setLookupsVersion((v) => v + 1); }}
      />

      <BulkImportDialog
        open={bulkImportOpen}
        onClose={() => setBulkImportOpen(false)}
        onImported={(result) => {
          showSnack('Activity created from the imported file.');
          dispatch(loadEntries(vendor));

          // Abre automaticamente a atividade acabada de criar, para o
          // utilizador poder rever/podar o Site List sem ter de a procurar
          // na tabela — só se tiver deixado marcada a opção "Auto-open
          // after creation" no Bulk Import.
          //
          // IMPORTANTE: ao contrário de handleRowClick (que já tem a linha
          // completa da tabela para mostrar de imediato), aqui só temos o
          // id — a atividade acabou de ser criada e ainda não está na
          // lista local. Por isso NÃO fazemos dispatch(openModal({ id }))
          // antes do loadEntry: isso punha modalOpen=true com um entry
          // praticamente vazio ({ id } apenas) ANTES de loadingOne passar a
          // true, e o TrackerModal calculava entryReady = !loadingOne como
          // true nesse instante — tentava renderizar o formulário completo
          // com technology/vendor/servers todos undefined. loadEntry por si
          // só já abre o modal (FETCH_ONE_SUCCESS define modalOpen=true) só
          // quando os dados completos chegam, sem esse render intermédio
          // partido.
          if (result?.entryId && result?.autoOpen) {
            dispatch(loadEntry(result.entryId));
          }
        }}
      />

      {/*
        SiteListDialog renderizado aqui, ao lado do TrackerModal — nunca dentro.
        Assim os dois Dialogs são independentes no DOM e o fecho de um
        nunca afecta o outro.
      */}
      <SiteListDialog
        open={siteListOpen}
        server={siteListServer}
        onClose={handleCloseSiteList}
        onConfirm={handleConfirmSiteList}
      />

      {/* ── Botão de ação flutuante (substitui os 4 botões do topo) ──────────── */}
      <TrackerFab
        hidden={modalOpen || settingsOpen || bulkImportOpen || siteListOpen}
        actions={[
          ...(canCreate ? [{
            key: 'new', label: 'New Activity', primary: true,
            icon: <AddIcon />, onClick: handleAddNew,
          }] : []),
          ...(canImport ? [{
            key: 'import', label: 'Bulk Import',
            icon: <UploadFileIcon />, onClick: () => setBulkImportOpen(true),
          }] : []),
          ...((role === 'Admin' || role === 'Manager') ? [{
            key: 'settings', label: 'Settings',
            icon: <TuneIcon />, onClick: () => setSettingsOpen(true),
          }] : []),
          {
            key: 'exportVisible', label: isFiltered ? `Export Visible (${displayedEntries.length})` : 'Export Visible',
            icon: <FilterListIcon />, onClick: handleExportVisible,
            disabled: exporting || displayedEntries.length === 0,
          },
          {
            key: 'export', label: 'Export All',
            icon: <DownloadIcon />, onClick: handleExportAll,
            disabled: exporting || entries.length === 0,
          },
        ]}
      />

      {/* ── Snackbar de feedback ─────────────────────────────────────────────── */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={closeSnack}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

    </Box>
  );
};

export default TrackerPage;